/* eslint-disable object-shorthand */
/* eslint-disable prefer-destructuring */
/**
 * Main application charts
 */
import "./style.css";
import MDBox from "components/Basics/MDBox";
import DashboardLayout from "components/Advanced/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Advanced/Navbars/DashboardNavbar";
import { useEffect, useState, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChartsActions from "redux-react/actions/chartsActions";
import SettingsActions from "redux-react/actions/settingsActions";
import { Divider, GridLegacy as Grid, Icon, Tooltip, Fade, Fab } from "@mui/material";
import { useMaterialUIController } from "context";
import ChartsLoader from "components/Custom/ChartsLoader";
import { getLocalStorageBackValues } from "components/Custom/Filters/filters";
import { wsEvent } from "redux-react/actions/ws";
import { socket } from "redux-react/middleware/ws";
import i18n from "i18n";
import lod_ from "lodash";
import { display } from "redux-react/reducers/snackBarReducer";
import CrossTable from "./crossTable";
import ComparisonChart from "./comparisonChart";
import FloatingActionsMenu from "./Components/FloatingActionsMenu";
import ChartEditingOrder from "./Components/ChartEditingOrder";
import OneValueDisplay from "./oneValueChart/display";
import ProportionChartDisplay from "./proportionChart/display";
import ReviewChartDisplay from "./reviewChart/display";
import DatasetViewPannel from "./Components/DatasetViewPannel";
import { queryDictionary } from "helpers/dictionary";
import MDTypography from "components/Basics/MDTypography";
import useFilters from "hooks/useFilters";
import ImportChartsDialog from "./ImportChartsDialog";
import GenerateChartsDialog from "./GenerateChartsDialog";

/**
 * Default component to display charts, works with dynamic datas
 */
export default function ChartsPage({
	route,
	// Chart
	handleEditChart: handleEditChartProps,
	handleAddChart: handleAddChartProps
}) {
	const UPDATE_PAGE_CHART = `updatePageChart_${route.route}`;

	const dispatch = useDispatch();
	const profile = useSelector(state => state.profile);
	const filters = useSelector(state => state.filters);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	// Page Charts
	const [charts, setCharts] = useState([]);

	// Copy to detect changes
	// null for the first load
	const [pageFiltersCopy, setPageFiltersCopy] = useState(undefined);
	const [hasPageFiltersChanged, setHasPageFiltersChanged] = useState(false);

	// Generate filters components
	const { component: filtersComponents, loaded: filtersLoaded, pageFilters } = useFilters(route);

	/**
	 * Detect changes in page filters
	 */
	const detectFiltersChange = () => {
		// Wait for filters to be loaded
		if (filtersLoaded !== true) {
			setHasPageFiltersChanged(false);
			return;
		}

		const isEqual = lod_.isEqual(pageFilters, pageFiltersCopy);

		setHasPageFiltersChanged(!isEqual);
	};

	/**
	 * Page edition
	 */
	const [pageEditMode, setPageEditMode] = useState(false);

	// Dataset pannel controller
	const [datasetViewPannel, setDatasetViewPannel] = useState({
		open: false,
		data: {}
	});

	const [importFromCatalogDialog, setImportFromCatalogDialog] = useState({
		open: false
	});

	const [generateChartDialog, setGenerateChartDialogDialog] = useState({
		open: false
	});

	/**
	 * Get charts data to be displayed in front
	 */
	const getChartsData = typeList => {
		const filteredCharts = charts.filter(chart =>
			typeList.includes(chart.customType || chart.type)
		);
		const sortedCharts = filteredCharts.sort((a, b) => a.display.rank - b.display.rank);
		return sortedCharts;
	};

	const setPlaceHolderCharts = useCallback(charts => {
		setCharts(charts);
	}, []);

	const updatePageChart = useCallback(chart => {
		setCharts(prevCharts => {
			return prevCharts.map(pChart => {
				if (pChart.code === chart.code) {
					return {
						...pChart,
						data: chart.data,
						loaded: true,
						dictionary: chart.dictionary,
						resource: chart.resource
					};
				}
				return pChart;
			});
		});
	}, []);

	const handleImportChart = () => {
		setImportFromCatalogDialog({ open: true });
	};

	const handleGenerateChart = () => {
		setGenerateChartDialogDialog({ open: true });
	};

	/**
	 * Handle click on chart's part
	 * @param {object} chart - Chart object
	 * @param {object} dataset - Dataset object of the clicked part
	 * @param {Array<object>} data - Full chart datas
	 */
	const clickHandler = useCallback(
		async (chart, dataset, data) => {
			const attributeValue = dataset.name;
			const attributeName = chart.request?.group ?? chart.request?.attribute;

			// Get page filters
			let actualFilters = getLocalStorageBackValues(
				profile.selectedAssistant.assistantID,
				route.route,
				filters
			);

			// Create the query filter for the dataset part
			let datasetQuery = {};

			switch (attributeValue) {
				case undefined:
				case null:
					// Do nothing
					break;
				case "other": {
					// Other => everything that is not in the list
					let notInArray = data.map(item => item.name).filter(name => name !== "other");
					datasetQuery = {
						[attributeName]: { $nin: notInArray }
					};
					break;
				}
				default:
					// Check if it's a number
					if (!Number.isNaN(Number(attributeValue))) {
						datasetQuery = {
							[attributeName]: { $in: [attributeValue, Number(attributeValue)] }
						};
					} else {
						datasetQuery = {
							[attributeName]: attributeValue
						};
					}
					break;
			}

			const resource = chart.resource;

			if (lod_.isNil(resource) || lod_.isEmpty(resource)) {
				return;
			}

			const dictionary = await queryDictionary(dispatch, chart.request?.collection);

			if (lod_.isNil(dictionary) || lod_.isEmpty(dictionary)) {
				return;
			}

			const items = dictionary.items ?? {};
			const displayedItems = Object.entries(items)
				.map(([itemKey, itemValue]) => {
					if (itemValue.display === true) {
						return itemKey;
					} else {
						return null;
					}
				})
				.filter(v => v !== null);

			let chartList = {
				documentType: "chart",
				type: "paginedList",
				request: {
					collection: resource.storage.collection,
					attributes: ["_id", "code", ...displayedItems],
					attributesDisplay: displayedItems
				},
				filters: {},
				options: {},
				pagination: {
					entriesPerPage: [50, 100, 200],
					defaultEntrie: 100
				},
				dictionary
			};

			const filtersChart = {
				...chart.filters,
				...datasetQuery,
				active: { $ne: false }
			};

			const filterByAgent = route?.filterByAgent ?? false;

			chartList.filters = lod_.merge(chartList.filters, filtersChart);

			dispatch(
				ChartsActions.buildRawCharts(
					[
						{
							documentType: chartList.documentType,
							filters: chartList.filters,
							options: chartList.options,
							request: chartList.request,
							pagination: chartList.pagination,
							type: chartList.type
						}
					],
					actualFilters,
					[],
					filterByAgent,
					res => {
						if (!res.charts || res.charts.length === 0) {
							return;
						}
						const buildedChart = res.charts[0];

						setDatasetViewPannel({
							open: true,
							data: {
								filters: actualFilters,
								chart: buildedChart,
								dataset,
								resource,
								dictionary
							}
						});
					}
				)
			);
		},
		[route, filters, dispatch, profile.selectedAssistant.assistantID]
	);

	/**
	 * Reorder the charts
	 * @param {<string>} type - Types of the charts
	 * @param {number} from - From index
	 * @param {number} to - To index
	 */
	function handleReorderChart(type, from, to) {
		// 1- Get the current charts
		const currentCharts = getChartsData(type);

		// 2- Clone the current charts
		const reorderedCharts = lod_.cloneDeep(currentCharts);
		// 3- Swap the charts
		const [removed] = reorderedCharts.splice(from, 1);
		reorderedCharts.splice(to, 0, removed);

		// 4- Get the codes of the reordered charts
		const reorderedChartsCodes = reorderedCharts.map(chart => chart.code);

		// 5- Set the rank of the reordered charts
		const reorderedChartsWithRank = reorderedCharts.map((chart, index) => {
			// Set the rank
			chart.display.rank = index;
			// Update the chart in the backend
			dispatch(
				SettingsActions.editSetting(
					profile.assistantID,
					"chart",
					{
						code: chart.code,
						"display.rank": index
					},
					() => {}
				)
			);

			return chart;
		});

		// 6- Update the charts
		setCharts(prevCharts => {
			return prevCharts.map(pChart => {
				if (reorderedChartsCodes.includes(pChart.code)) {
					return reorderedChartsWithRank.find(chart => chart.code === pChart.code);
				}
				return pChart;
			});
		});

		// reorder in back
	}

	/**
	 * Drag handler
	 * @param {*} param0
	 * @returns
	 */
	const dragHandler = useCallback(
		({ type, from, to }) => {
			if (from === to) return;
			handleReorderChart(type, from, to);
		},
		[charts]
	);

	const loadCharts = () => {
		dispatch(
			wsEvent({
				event: "getChartsForPage",
				payload: {
					isSystem: route.system ?? false,
					route: route.route,
					filterByAgent: route?.filterByAgent ?? false,
					wsEventResult: UPDATE_PAGE_CHART,
					filters: pageFilters
				}
			})
		);
	};

	const handleDeleteChart = useCallback(
		code => {
			const onSuccess = res => {
				dispatch(
					display({
						message: i18n.t("SETTINGS.CHARTS.SUCCESS.delete"),
						type: "success"
					})
				);
				loadCharts();
			};
			dispatch(
				SettingsActions.deleteSetting(profile.assistantID, "chart", { code: code }, onSuccess)
			);
		},
		[dispatch, profile.assistantID]
	);

	const handleEditChart = useCallback(
		code => {
			handleEditChartProps(code, (editedChart, edit) => {
				if (edit) {
					const onSuccess = res => {
						dispatch(
							display({
								message: i18n.t("SETTINGS.CHARTS.SUCCESS.edit"),
								type: "success"
							})
						);
						loadCharts();
					};
					dispatch(
						SettingsActions.editSetting(profile.assistantID, "chart", editedChart, onSuccess)
					);
				}
			});
		},
		[handleEditChartProps, dispatch, profile.assistantID]
	);

	const handleAddChart = useCallback(() => {
		handleAddChartProps(
			{
				"display.pages": [route.route.replace("/", "")]
			},
			(editedChart, edit) => {
				const onSuccess = res => {
					dispatch(
						display({
							message: i18n.t("SETTINGS.CHARTS.SUCCESS.add"),
							type: "success"
						})
					);
					loadCharts();
				};
				dispatch(SettingsActions.newSetting(profile.assistantID, "chart", editedChart, onSuccess));
			}
		);
	}, []);

	useEffect(() => {
		if (filtersLoaded === true && pageFiltersCopy === undefined) {
			// First load
			setPageFiltersCopy(lod_.cloneDeep(pageFilters));
		}
		detectFiltersChange();
	}, [pageFilters, filtersLoaded, pageFiltersCopy]);

	useEffect(() => {
		setCharts([]);
		setPageFiltersCopy(undefined);
		/**
		 * WS Event
		 */
		socket.on(UPDATE_PAGE_CHART, updatePageChart);
		socket.on(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);

		return () => {
			socket.off(UPDATE_PAGE_CHART, updatePageChart);
			socket.off(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);

			socket.emit(`stopChartsProcessing`, {
				wsEventResult: UPDATE_PAGE_CHART
			});
		};
	}, [profile.selectedAssistant.assistantID, route.route]);

	useEffect(() => {
		if (filtersLoaded === true) {
			loadCharts();
		}
	}, [filtersLoaded]);

	/* Charts loader */
	if (!filtersLoaded) {
		return (
			<DashboardLayout>
				<MDBox>
					<DashboardNavbar />
					<ChartsLoader darkMode={darkMode} />
				</MDBox>
			</DashboardLayout>
		);
	} else {
		/* Main component */
		return (
			<DashboardLayout>
				{/*  */}
				<Fade in={hasPageFiltersChanged}>
					<div
						style={{
							position: "fixed",
							bottom: 20,
							right: 20,
							zIndex: 1500
						}}
					>
						<Fab
							variant="extended"
							color="info"
							className="fab-glow"
							onClick={() => {
								// Save current page filters as copy
								setPageFiltersCopy(lod_.cloneDeep(pageFilters));
								setHasPageFiltersChanged(false);
								loadCharts();
							}}
						>
							<MDTypography variant="h6" color="light">
								{i18n.t("SETTINGS.RULES.detectedModifications")}
							</MDTypography>
							<MDBox
								bgColor="light"
								p={1}
								borderRadius="lg"
								fontWeight="bold"
								display="flex"
								alignItems="center"
								ml={2}
							>
								<Icon sx={{ mr: 1 }} fontSize="medium">
									save
								</Icon>
								&nbsp;{i18n.t("SETTINGS.apply")}
							</MDBox>
						</Fab>
					</div>
				</Fade>
				{/* Import from catalog dialog */}
				<ImportChartsDialog
					open={importFromCatalogDialog.open}
					onClose={() => setImportFromCatalogDialog({ open: false })}
					currentPage={route.route.replace("/", "")}
					reloadPage={() => {
						loadCharts();
						setImportFromCatalogDialog({ open: false });
					}}
				/>

				{/* Generate chart with AI dialog */}
				<GenerateChartsDialog
					open={generateChartDialog.open}
					onClose={() => setGenerateChartDialogDialog({ open: false })}
					currentPage={route.route.replace("/", "")}
					reloadPage={() => {
						loadCharts();
						setGenerateChartDialogDialog({ open: false });
					}}
				/>

				{/* Drawer for dataset view */}
				{datasetViewPannel?.open && (
					<DatasetViewPannel
						{...datasetViewPannel}
						onClose={() =>
							setDatasetViewPannel({
								open: false,
								data: {}
							})
						}
					/>
				)}

				{/* Page edition */}
				<FloatingActionsMenu
					edit={pageEditMode}
					handleAddChart={handleAddChart}
					handleImportChart={handleImportChart}
					handleGenerateChart={handleGenerateChart}
					onClick={() => {
						setPageEditMode(!pageEditMode);
					}}
				/>

				<MDBox>
					<DashboardNavbar
						filters={[
							<MDBox display="flex">
								{route?.filterByAgent && (
									<Tooltip placement="top" title={i18n.t("SETTINGS.PAGES.filterByAgentPage")}>
										<Icon fontSize="large">person</Icon>
									</Tooltip>
								)}
								{filtersComponents}
							</MDBox>
						]}
					/>

					<MDBox mt={4}>
						{/* Review */}
						<ReviewChartDisplay
							pageEditMode={pageEditMode}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
						/>
						{/* One value */}
						<OneValueDisplay
							charts={charts}
							setCharts={setCharts}
							pageEditMode={pageEditMode}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={clickHandler}
						/>
						{/* Proportion graphs */}
						<ProportionChartDisplay
							pageEditMode={pageEditMode}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={clickHandler}
						/>
						{/* Comparison table */}
						<ComparisonTableDisplay
							pageEditMode={pageEditMode}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={clickHandler}
						/>
						{/* Cross Tables */}
						<CrossTableDisplay
							pageEditMode={pageEditMode}
							getChartsData={getChartsData}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							handleDeleteChart={handleDeleteChart}
							clickHandler={clickHandler}
						/>
					</MDBox>
				</MDBox>
			</DashboardLayout>
		);
	}
}

/**
 * Display comparison charts
 * @param {*} param0
 * @returns
 */
const ComparisonTableDisplay = memo(
	({ pageEditMode, getChartsData, dragHandler, handleEditChart, handleDeleteChart }) => {
		const LIST = ["comparison"];

		const [hoveredChart, setHoveredChart] = useState(null);

		const editingMouseInCard = code => {
			setHoveredChart(code);
		};

		const editingMouseOutCard = code => {
			setHoveredChart(null);
		};

		const isHovered = code => {
			return hoveredChart === code;
		};

		return (
			<>
				<MDBox display="flex" flexDirection="row" mt={4}>
					{pageEditMode && (
						<ChartEditingOrder
							getChartsData={getChartsData}
							type={LIST}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							mouseIn={editingMouseInCard}
							mouseOut={editingMouseOutCard}
							handleDeleteChart={handleDeleteChart}
						/>
					)}
					<MDBox
						flex="5"
						style={{
							width: "100%",
							overflowX: "auto"
						}}
					>
						<Grid container spacing={3} columns={{ xs: 2, sm: 2, md: 4, xxl: 6 }}>
							{getChartsData(LIST).map((chart, index) => {
								return (
									<ComparisonChart
										id={chart.code}
										key={index}
										chart={chart}
										data={chart.data}
										mt={5}
										xs={2}
										md={4}
										lg={2}
										xxl={6}
										style={{
											filter: isHovered(chart.code) ? "brightness(0.8)" : ""
										}}
									/>
								);
							})}
						</Grid>
					</MDBox>
				</MDBox>
				{pageEditMode && getChartsData(LIST).length > 0 && <Divider mt={10} mb={10} />}
			</>
		);
	}
);

/**
 * Cross table display
 * @returns
 */
const CrossTableDisplay = memo(
	({ pageEditMode, getChartsData, dragHandler, handleEditChart, handleDeleteChart }) => {
		const LIST = ["crossTable", "timeSeries", "multiCollection"];

		const [hoveredChart, setHoveredChart] = useState(null);

		const editingMouseInCard = code => {
			setHoveredChart(code);
		};

		const editingMouseOutCard = code => {
			setHoveredChart(null);
		};

		const isHovered = code => {
			return hoveredChart === code;
		};

		return (
			<>
				<MDBox display="flex" flexDirection="row">
					{pageEditMode && (
						<ChartEditingOrder
							getChartsData={getChartsData}
							type={LIST}
							dragHandler={dragHandler}
							handleEditChart={handleEditChart}
							mouseIn={editingMouseInCard}
							mouseOut={editingMouseOutCard}
							handleDeleteChart={handleDeleteChart}
						/>
					)}
					<MDBox
						flex="5"
						style={{
							width: "100%",
							overflowX: "auto"
						}}
					>
						<Grid container spacing={3} columns={{ xs: 2, sm: 2, md: 4, xxl: 6 }}>
							{getChartsData(LIST).map((chart, index) => {
								return (
									<CrossTable
										id={chart.code}
										chart={chart}
										data={chart.data}
										key={index}
										mt={5}
										xs={2}
										md={4}
										lg={2}
										xxl={6}
										style={{
											filter: isHovered(chart.code) ? "brightness(0.8)" : ""
										}}
									/>
								);
							})}
						</Grid>
					</MDBox>
				</MDBox>
				{pageEditMode && getChartsData(LIST).length > 0 && <Divider mt={10} mb={10} />}
			</>
		);
	}
);
